import type React from 'react'
import type { PlanStatus } from '@knownow/domain'

export type FilterKey = 'all' | PlanStatus

export interface FilterOption {
  readonly key: FilterKey
  readonly label: string
  readonly count: number
}

export interface FilterToolbarProps {
  readonly options: readonly FilterOption[]
  readonly activeKey: FilterKey
  readonly onSelect: (key: FilterKey) => void
  readonly searchQuery: string
  readonly onSearchChange: (q: string) => void
  readonly viewMode: 'card' | 'list'
  readonly onViewModeChange: (mode: 'card' | 'list') => void
}

function SearchIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function GridIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  )
}

function ListIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  )
}

export function FilterToolbar(props: FilterToolbarProps): React.ReactElement {
  const { options, activeKey, onSelect, searchQuery, onSearchChange, viewMode, onViewModeChange } = props

  return (
    <div className="flex flex-wrap items-center gap-3 pb-4" data-testid="filter-toolbar">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter plans">
        {options.map((opt) => {
          const selected = opt.key === activeKey
          return (
            <button
              key={opt.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(opt.key)}
              data-testid={`filter-${opt.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                selected
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-surface-elevated text-foreground hover:bg-muted'
              }`}
            >
              <span>{opt.label}</span>
              <span className={selected ? 'text-background/70' : 'text-muted-foreground'}>
                {opt.count}
              </span>
            </button>
          )
        })}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-2 rounded-full border border-border bg-surface-elevated px-3 py-1 text-sm text-foreground">
          <span className="text-muted-foreground"><SearchIcon /></span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search plans..."
            aria-label="Search plans"
            className="w-44 bg-transparent outline-none placeholder:text-muted-foreground"
            data-testid="plans-search"
          />
        </label>

        <div
          className="inline-flex overflow-hidden rounded-full border border-border bg-surface-elevated"
          role="group"
          aria-label="View mode"
        >
          <button
            type="button"
            onClick={() => onViewModeChange('card')}
            aria-pressed={viewMode === 'card'}
            data-testid="view-card"
            className={`flex h-7 w-8 items-center justify-center ${
              viewMode === 'card' ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted'
            }`}
          >
            <GridIcon />
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange('list')}
            aria-pressed={viewMode === 'list'}
            data-testid="view-list"
            className={`flex h-7 w-8 items-center justify-center ${
              viewMode === 'list' ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted'
            }`}
          >
            <ListIcon />
          </button>
        </div>
      </div>
    </div>
  )
}
